import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, ElementRef, inject, input, model, resource, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ChitinIconComponent } from '@shared/components/chitin/atoms/icon/chitin-icon.component';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { getDocument, GlobalWorkerOptions, PDFPageProxy, ResponseException } from 'pdfjs-dist';
GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  imports: [CommonModule, ChitinIconComponent, LoadingSpinnerComponent],
})
export class PdfViewerComponent {
  private readonly destroyRef = inject(DestroyRef);
  fileUrl = input.required<string>();
  renderCanvas = viewChild<ElementRef<HTMLCanvasElement>>('renderCanvas');

  pdfProxy = resource({
    params: () => ({ fileUrl: this.fileUrl() }),
    loader: ({ params }) => getDocument(params.fileUrl).promise,
  });
  pageCount = computed<number | null>(() => {
    if (!this.pdfProxy.hasValue()) return null;
    return this.pdfProxy.value()?.numPages ?? null;
  });
  errorMessage = computed<string | null>(() => {
    const error = this.pdfProxy.error();
    if (!error) return null;
    const statusCode = (error as unknown as ResponseException).status;
    if ([404, 403].includes(statusCode)) return 'File not found. Please contact customer support for assistance.';
    return 'An unknown error occurred. Please contact customer support for assistance.';
  });
  isLoading = computed<boolean>(() => this.pdfProxy.isLoading() || this.currentPage.isLoading());
  currentPageNumber = model<number>(1);
  currentPage = resource({
    params: () => ({ pageNumber: this.currentPageNumber(), pdfProxy: this.pdfProxy.hasValue() ? this.pdfProxy.value() : null }),
    loader: ({ params }) => params.pdfProxy?.getPage(params.pageNumber) ?? Promise.resolve(null),
  });

  constructor() {
    toObservable(this.currentPage.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(page => {
        if (!page) return;
        this.renderPage(page);
      });
  }

  private renderPage(page: PDFPageProxy) {
    const viewport = page.getViewport({ scale: 1 });
    const canvas = this.renderCanvas();
    if (!canvas) return;

    const context = canvas.nativeElement.getContext('2d') ?? undefined;
    canvas.nativeElement.width = Math.floor(viewport.width);
    canvas.nativeElement.height = Math.floor(viewport.height);

    const renderContext = {
      canvas: canvas.nativeElement,
      canvasContext: context,
      viewport: viewport,
    };
    page.render(renderContext);
  }
}
